import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import type { PricingEventAttribution, ProximityDisplay } from "~/types/search";

export interface AppliedCoupon {
  code: string;
  coupon_name: string;
  discount_amount: number;
  driver_payable_amount: number;
  payment_requirement: "payment_required" | "no_payment_required";
}

export interface ExtensionContext {
  booking_id: string;
  booking_confirmation_code: string;
  /** The spot being extended — used to load its name and amenities. */
  listing_id: string;
  /** Vehicle locked to the booking — shown read-only on the extension checkout. */
  vehicle_id: string;
  hold_id: string;
  starts_at: string;
  ends_at: string;
  expires_at: string;
  base_amount: number;
  fee_amount: number;
  tax_amount: number;
  total_amount: number;
  // Proximity to the driver's destination, carried over from the booking so the
  // extension checkout can show the same "5 min walk · 0.3 mi" detail.
  walking_minutes?: number | null;
  distance_miles?: number | null;
  proximity_display?: ProximityDisplay | null;
  coupon?: AppliedCoupon | null;
  /**
   * The event that priced the extension, if any — from the extension hold's own
   * frozen breakdown. Extending *into* an event window is exactly where a price
   * jump surprises a driver, so the notice matters here as much as at booking.
   */
  event?: PricingEventAttribution | null;
  // Spot summary carried from the original booking's `spot_details` (getOne is
  // host-only now). `display_name` is spot-type-derived, not the host name.
  display_name?: string | null;
  /** The spot's IANA zone — see `BookingContext.timezone`. */
  timezone?: string | null;
  spot_type?: string;
  ev_charger?: boolean;
  gated?: boolean;
  security_camera?: boolean;
  // Private address — only carried when the booking's location is revealed
  // (present on `spot_details.location`); undefined while still hidden.
  street_address?: string;
  unit_number?: string | null;
  city?: string;
  state?: string;
  zip?: string;
  /**
   * Set at a confirm's first server write for this hold (the PaymentIntent, or
   * the $0 confirm) and never cleared; a new hold replaces the whole context,
   * which resets it. Once set, release callers skip the release: the backend
   * may already have converted the hold (a lost response looks like a
   * failure), and an unreleased hold just expires.
   */
  confirm_started?: boolean;
}

interface ExtensionStore {
  context: ExtensionContext | null;
  setContext: (ctx: ExtensionContext) => void;
  setCoupon: (coupon: AppliedCoupon | null) => void;
  /** Marks the current hold's confirm as sent. See `confirm_started`. */
  markConfirmStarted: () => void;
  clear: () => void;
}

export const useExtensionStore = create<ExtensionStore>()(
  persist(
    (set) => ({
      context: null,
      setContext: (context) => set({ context }),
      setCoupon: (coupon) =>
        set((s) => ({
          context: s.context ? { ...s.context, coupon } : null,
        })),
      markConfirmStarted: () =>
        set((s) => ({
          context: s.context ? { ...s.context, confirm_started: true } : null,
        })),
      clear: () => set({ context: null }),
    }),
    {
      name: "ezspot_extension_hold",
      storage: createJSONStorage(() => sessionStorage),
      onRehydrateStorage: () => (state) => {
        if (
          state?.context &&
          new Date(state.context.expires_at).getTime() <= Date.now()
        ) {
          state.clear();
        }
      },
    },
  ),
);
